import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, MenuItem, FormControl, InputLabel,
  Select, IconButton, CircularProgress, Pagination, Chip, Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import { inventoryMovementService } from '../services/api';
import { orderRefFromRow } from '../utils/orderLabel';

const PAGE_SIZE = 50;

/** Типи операцій з InventoryMovement.ACTION_CHOICES */
const ACTION_OPTIONS = [
  { value: '', label: 'Усі операції' },
  { value: 'stock_receipt', label: 'Прихід сировини' },
  { value: 'fg_receipt', label: 'Прихід ГП' },
  { value: 'fg_shipment', label: 'Відвантаження ГП (вибуття)' },
  { value: 'transfer', label: 'Переміщення' },
  { value: 'mix_add', label: 'Заміс' },
  { value: 'consume', label: 'Автосписання у виробництво' },
  { value: 'writeoff', label: 'Списання (завершення)' },
  { value: 'other', label: 'Інше' },
];

/** Склади з InventoryMovement.WAREHOUSE_CHOICES (коди в журналі) */
const WAREHOUSE_OPTIONS = [
  { value: '', label: 'Усі склади' },
  { value: 'main', label: 'Склад сировини' },
  { value: 'production', label: 'Склад виробництва' },
  { value: 'finished_goods', label: 'Готова продукція' },
  { value: 'mixing', label: 'Заміс' },
  { value: 'consumed', label: 'Списано у виробництво' },
  { value: 'external', label: 'Зовні (постачальник/клієнт)' },
  { value: 'waste', label: 'Відходи' },
  { value: 'crushing', label: 'Дробарка' },
];

function formatDt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('uk-UA', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function InventoryMovementsPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState('');
  const [fromWarehouse, setFromWarehouse] = useState('');
  const [toWarehouse, setToWarehouse] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, ordering: '-created_at' };
      if (action) params.action = action;
      if (fromWarehouse) params.from_warehouse = fromWarehouse;
      if (toWarehouse) params.to_warehouse = toWarehouse;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (searchApplied.trim()) params.search = searchApplied.trim();
      const res = await inventoryMovementService.getAll(params);
      const data = res.data;
      if (Array.isArray(data)) {
        setRows(data);
        setTotal(data.length);
      } else {
        setRows(data?.results || []);
        setTotal(data?.count || 0);
      }
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, action, fromWarehouse, toWarehouse, dateFrom, dateTo, searchApplied]);

  useEffect(() => { load(); }, [load]);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const clearFilters = () => {
    setAction('');
    setFromWarehouse('');
    setToWarehouse('');
    setDateFrom('');
    setDateTo('');
    setSearch('');
    setSearchApplied('');
    setPage(1);
  };

  const hasFilters = !!(action || fromWarehouse || toWarehouse || dateFrom || dateTo || searchApplied);

  return (
    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ flex: 1, minWidth: 180 }}>
          Журнал рухів складу
        </Typography>
        <TextField
          size="small"
          type="date"
          label="Дата від"
          value={dateFrom}
          onChange={(e) => { setPage(1); setDateFrom(e.target.value); }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 150 }}
        />
        <TextField
          size="small"
          type="date"
          label="Дата до"
          value={dateTo}
          onChange={(e) => { setPage(1); setDateTo(e.target.value); }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 150 }}
        />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Тип операції</InputLabel>
          <Select
            label="Тип операції"
            value={action}
            onChange={(e) => { setPage(1); setAction(e.target.value); }}
          >
            {ACTION_OPTIONS.map((o) => (
              <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Склад виходу</InputLabel>
          <Select
            label="Склад виходу"
            value={fromWarehouse}
            onChange={(e) => { setPage(1); setFromWarehouse(e.target.value); }}
          >
            {WAREHOUSE_OPTIONS.map((o) => (
              <MenuItem key={`from-${o.value || 'all'}`} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Склад входу</InputLabel>
          <Select
            label="Склад входу"
            value={toWarehouse}
            onChange={(e) => { setPage(1); setToWarehouse(e.target.value); }}
          >
            {WAREHOUSE_OPTIONS.map((o) => (
              <MenuItem key={`to-${o.value || 'all'}`} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Пошук (назва / примітка)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setPage(1);
              setSearchApplied(search);
            }
          }}
          sx={{ minWidth: 200 }}
        />
        <Button
          size="small"
          variant="outlined"
          onClick={() => { setPage(1); setSearchApplied(search); }}
        >
          Знайти
        </Button>
        {hasFilters && (
          <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>
            Скинути
          </Button>
        )}
        <IconButton onClick={load} disabled={loading} title="Оновити">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Typography variant="caption" color="text.secondary">
        Фільтри за періодом, типом операції та складами (вихід / вхід) працюють разом.
        Показує нові рухи з моменту впровадження журналу.
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, minHeight: 0 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Коли</TableCell>
              <TableCell>Операція</TableCell>
              <TableCell>Матеріал</TableCell>
              <TableCell>Звідки</TableCell>
              <TableCell>Куди</TableCell>
              <TableCell align="right">К-сть</TableCell>
              <TableCell>ЗВ</TableCell>
              <TableCell>Примітка</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  Немає записів за обраними фільтрами.
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDt(r.created_at)}</TableCell>
                <TableCell>
                  <Chip size="small" label={r.action_display || r.action} />
                </TableCell>
                <TableCell>{r.display_name || r.material_name || '—'}</TableCell>
                <TableCell>{r.from_warehouse_display || r.from_warehouse}</TableCell>
                <TableCell>{r.to_warehouse_display || r.to_warehouse}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                  {Number(r.quantity).toLocaleString('uk-UA', { maximumFractionDigits: 4 })} {r.unit}
                </TableCell>
                <TableCell>{r.order_id != null ? orderRefFromRow(r) : '—'}</TableCell>
                <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.note || '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {pages > 1 && (
        <Pagination
          count={pages}
          page={page}
          onChange={(_, p) => setPage(p)}
          color="primary"
          sx={{ alignSelf: 'center' }}
        />
      )}
    </Box>
  );
}
